import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const TIMEFRAME_OPTIONS = [
  { id: "daily" as const, label: "Últimos 7 días", granularity: "day" as const },
  { id: "weekly" as const, label: "Últimas 8 semanas", granularity: "week" as const },
  { id: "monthly" as const, label: "Últimos 12 meses", granularity: "month" as const },
];

export const INTEGRATION_OPTIONS = [
  { value: "all", label: "Todas las integraciones" },
  { value: "twitch", label: "Twitch" },
  { value: "youtube", label: "YouTube" },
  { value: "kick", label: "Kick" },
];

interface StatsFiltersProps {
  timeframe: "daily" | "weekly" | "monthly";
  onTimeframeChange: (value: "daily" | "weekly" | "monthly") => void;
  dateRange: { start: string; end: string };
  onDateRangeChange: (range: { start: string; end: string }) => void;
  timeRange: { start: string; end: string };
  onTimeRangeChange: (range: { start: string; end: string }) => void;
  selectedIntegration?: string;
  onIntegrationChange?: (value: string) => void;
  showIntegrationFilter?: boolean;
}

export const StatsFilters = ({
  timeframe,
  onTimeframeChange,
  dateRange,
  onDateRangeChange,
  timeRange,
  onTimeRangeChange,
  selectedIntegration,
  onIntegrationChange,
  showIntegrationFilter = false,
}: StatsFiltersProps) => {
  return (
    <Card className="border-neutral-800 bg-neutral-900/50">
      <CardContent className="flex flex-wrap gap-4 p-4">
        {/* Timeframe buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {TIMEFRAME_OPTIONS.map((option) => (
            <Button
              key={option.id}
              variant={option.id === timeframe ? "default" : "outline"}
              size="sm"
              onClick={() => onTimeframeChange(option.id)}
              className={
                option.id === timeframe
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "border-neutral-700 bg-neutral-800/50 text-neutral-300 hover:bg-neutral-700"
              }
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="date-start" className="text-xs text-neutral-400">
              Desde
            </Label>
            <Input
              id="date-start"
              type="date"
              value={dateRange.start}
              onChange={(e) => onDateRangeChange({ ...dateRange, start: e.target.value })}
              className="h-8 w-auto border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="date-end" className="text-xs text-neutral-400">
              Hasta
            </Label>
            <Input
              id="date-end"
              type="date"
              value={dateRange.end}
              onChange={(e) => onDateRangeChange({ ...dateRange, end: e.target.value })}
              className="h-8 w-auto border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200"
            />
          </div>
        </div>

        {/* Time range */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="time-start" className="text-xs text-neutral-400">
              Hora inicio
            </Label>
            <Input
              id="time-start"
              type="time"
              value={timeRange.start}
              onChange={(e) => onTimeRangeChange({ ...timeRange, start: e.target.value })}
              className="h-8 w-auto border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="time-end" className="text-xs text-neutral-400">
              Hora fin
            </Label>
            <Input
              id="time-end"
              type="time"
              value={timeRange.end}
              onChange={(e) => onTimeRangeChange({ ...timeRange, end: e.target.value })}
              className="h-8 w-auto border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200"
            />
          </div>
        </div>

        {/* Integration select */}
        {showIntegrationFilter && onIntegrationChange && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-neutral-400">Integración</Label>
            <Select value={selectedIntegration} onValueChange={onIntegrationChange}>
              <SelectTrigger className="h-8 w-[180px] border-neutral-700 bg-neutral-800/50 text-xs text-neutral-200">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent className="border-neutral-700 bg-neutral-900">
                {INTEGRATION_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-neutral-200 focus:bg-neutral-800"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
